import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Search, RefreshCw} from "lucide-react";
import { selectModuleTabs } from "../../../redux/selectors/manageTeamTabSelector";
import { addTab, removeTab, setActiveTab } from "../../../redux/slices/manageTeamSlice";
import Tabs from "../../../components/ACS-Dashboard/ACS-Blocks/Tabs";
import TeamTable from "./TabContent/TeamTable";
import Button from "../../../components/ACS-Dashboard/ACS-Blocks/Button";
import FilterButton from "../../../components/ACS-Dashboard/ACS-Blocks/FilterButton";
import OrgInvitesTab from "./TabContent/OrgInvitesTab";
import PreferedPartners from "./TabContent/PreferredPartners";
import AddChildOrganizationTab from "./TabContent/AddChildOrganizationTab";
import RoleManagement from "./TabContent/RoleManagement";
import BulkUploadTab from "./TabContent/BulkUpload";
import LocationManagement from "./TabContent/LocationManagement";
import { usePrivilege } from "../../../guard/usePrivilege";
import ViewLocation from "./TabContent/ViewTab/ViewLocation";
import AddLocationModal from "./Modals/AddLocationModal";
import ViewUserProfile from "./TabContent/ViewTab/ViewUserProfile";
import ViewPreferredPartnerDetails from "./TabContent/ViewTab/ViewPreferredPartnerDetails";
import Cookies from "js-cookie";
import { fetchAllRoles,searchUsers,searchOrganizations } from "./api";
import TeamDropdownContent from "./TeamDropdownContent"; // ✅ new nested filter dropdown
import notify from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";


const ManageTeamDetails = () => {
  const dispatch = useDispatch();
  const { tabs, activeTab, defaultTabsCount } = useSelector(selectModuleTabs("manageTeam"));
  const { hasPrivilege } = usePrivilege();
  const canAddTeamMember = hasPrivilege("addTeamMember:ADD");

  // ✅ NEW: Filter & dropdown state
  const [filters, setFilters] = useState({
    offices: [],
    roles: [],
    status: "",
    sortBy: "",
  });
  const [searchResults, setSearchResults] = useState(null);
  const [offices, setOffices] = useState([]);
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [reloading, setReloading] = useState(false);


  useEffect(() => {
    // Decode office codes from cookie
    const cookieVal = Cookies.get("officeCodes");
    if (cookieVal) {
      const decoded = decodeURIComponent(cookieVal).split(",");
      setOffices(decoded);
    }

    // Fetch roles using auth token
    const token = Cookies.get("authToken");
    fetchAllRoles(token)
      .then((res) => setRoles(res.data || []))
      .catch((err) => console.error("Failed to fetch roles", err));
  }, []);

  const handleCreateTeam = () => {
    if (!canAddTeamMember) {
      console.log("No permission to add team member");
      return;
    }
    const id = `create-${Date.now()}`;
    dispatch(
      addTab({
        id,
        name: "Add Team Member",
        contentType: "addTeam",
        isDynamic: true,
        createdAt: Date.now(),
      })
    );
    dispatch(setActiveTab({ index: tabs.length }));
  };

  const handleRemoveTab = (index) => {
    if (index < defaultTabsCount) {
      alert("Default tabs cannot be removed.");
      return;
    }
    dispatch(removeTab({ tabIndex: index, defaultTabsCount }));
  };

  const handleApplyFilters = async (filters) => {
    // 🧩 If filters are cleared → reset to default
    if (!filters) {
      setSearchResults(null); // ✅ TeamTable will now fallback to fetchTeamMembers()
      notify.info({
        title: "Filters Cleared",
        message: "Showing all team members.",
      });
      return;
    }

    try {
      const res = await searchUsers(filters);
      console.log("✅ Search API response:", res.data);

      setSearchResults(res.data.content || res.data);

      notify.success({
        title: "Search Completed",
        message: `Found ${res.data.content?.length || res.data.length || 0} matching team members.`,
      });
    } catch (err) {
      console.error("❌ Search failed:", err);
      notify.error({
        title: "Search Failed",
        message: err.response?.data?.message || "Unable to fetch search results. Please try again.",
      });
    }
  };
  const currentTab = tabs[activeTab];


  const handleSearch = async () => {
  if (!searchTerm.trim()) return;

  try {
    let res;

    if (currentTab?.id === "pref-partners") {
      // ✅ Use your new organization search API
      res = await searchOrganizations(searchTerm);
      console.log("✅ Partner search response:", res.data);
      setSearchResults(res.data.content || []);
      notify.success({
        title: "Search Completed",
        message: `Found ${res.data.content?.length || 0} matching partners.`,
      });
    } else {
      // ✅ Default: Team Members search
      res = await searchUsers({ searchTerm });
      console.log("✅ Team search response:", res.data);
      setSearchResults(res.data.content || res.data);
      notify.success({
        title: "Search Completed",
        message: `Found ${res.data.content?.length || res.data.length || 0} matching team members.`,
      });
    }
  } catch (err) {
    console.error("❌ Search failed:", err);
    notify.error({
      title: "Search Failed",
      message: "Unable to fetch search results.",
    });
  }
};






  return (
    <div className="p-fluid-1 shadow-md flex flex-col flex-1 h-full min-h-0 overflow-hidden">
      {/* === Top Action/Filter Bar === */}
      <div className="flex flex-wrap justify-between items-center rounded-md ml-[2] p-fluid-1">
        {/* === Left side: Filter + Search + Label === */}
        <div className="flex flex-wrap items-center gap-2">
          {/* ✅ Reusable Filter Button with nested dropdown */}
          <FilterButton
            text="Filter by"
            contentComponent={
              <TeamDropdownContent
                filters={filters}
                setFilters={setFilters}
                offices={offices}
                roles={roles}
                onApply={handleApplyFilters}
              />
            }
          />

          {/* === Search Bar === */}
          <div className="relative flex items-center border border-gray-300 bg-white rounded-full px-3 py-1.5 w-80">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder={
                currentTab?.id === "pref-partners"
                  ? "Search Partners"
                  : "Search Team"
              }
              className="w-full outline-none text-fluid-sm text-gray-800"
            />
            <button
              onClick={handleSearch}
              className="absolute right-3 text-gray-500 hover:text-gray-700"
            >
              <Search size={18} strokeWidth={2} />
            </button>
          </div>

          <span className="text-gray-500 text-fluid-sm">Team Members</span>
          <button
                      onClick={() => {
                        setReloading(true);           // spin the icon briefly
                        setTimeout(() => window.location.reload(), 150); // tiny delay for visual feedback
                      }}
                      className="p-1 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#223688]/30 transition"
                      title="Reload page"
                      aria-label="Reload page"
                    >
                      <RefreshCw
                        size={16}
                        className={`text-gray-500 text-acs-light-text dark:text-acs-dark-text ${reloading ? "animate-spin" : "hover:text-gray-700"}`}
                      />
                    </button>
        </div>

        {/* === Right side: Add button === */}
        <div className="flex items-center gap-2">
          {canAddTeamMember && (
            <Button
              text="Add Team Member"
              onClick={handleCreateTeam}
              backgroundColor="#223688"
              textColor="white"
              fontSize="14px"
              padding="10px 18px"
              borderRadius="24px"
            />
          )}
        </div>
      </div>

      {/* === Tab Content === */}
      <div className="flex-1 mt-fluid-2 min-h-0 flex flex-col scrollbar-hide">
        {tabs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 text-lg font-semibold">
            No tabs available or you do not have access.
          </div>
        ) : (
          <Tabs
            tabs={tabs.map((tab) => tab.name)}
            tabContents={tabs.map((tab) => {
              if (tab.contentType === "addTeam") {
                return (
                  <BulkUploadTab
                    key={tab.id}
                    onSuccess={(msg) => {
                      const tabIndex = tabs.findIndex((t) => t.id === tab.id);
                      if (tabIndex >= defaultTabsCount) handleRemoveTab(tabIndex);
                      dispatch(setActiveTab({ index: 0 }));
                    }}
                    onCancel={() =>
                      handleRemoveTab(tabs.findIndex((t) => t.id === tab.id))
                    }
                  />
                );
              }
              if (tab.id === "teamMembers")
                return <TeamTable key="teamMembers" data={searchResults} />;
              if (tab.id === "org-invites") return <OrgInvitesTab key="org-invites" />;
              if (tab.id === "pref-partners")
                return <PreferedPartners key="pref-partners" data={searchResults} />;
              if (tab.id === "child-org") return <AddChildOrganizationTab key="child-org" />;
              if (tab.id === "role-mgmt") return <RoleManagement key="role-mgmt" />;
              if (tab.id === "bulk-upload") return <BulkUploadTab key="bulk-upload" />;
              if (tab.id === "location-mgmt") return <LocationManagement key="location-mgmt" />;
              if (tab.contentType === "viewPreferredPartnerDetails")
                return <ViewPreferredPartnerDetails key={tab.id} orgData={tab.data} />;
              if (tab.contentType === "viewLocation")
                return (
                  <ViewLocation 
                    key={tab.id} 
                    location={tab.data}
                  />
                );
              if (tab.contentType === "viewUserProfile")
                return <ViewUserProfile key={tab.id} userData={tab.data} />;
              if (tab.contentType === "addLocation")
                return (
                  <AddLocationModal
                    key={tab.id}
                    locationRef={tab.data?.locationRef}
                    token={tab.data?.token}
                    onClose={() =>
                      handleRemoveTab(tabs.findIndex((t) => t.id === tab.id))
                    }
                  />
                );
              return <div key={tab.id}>Access Denied</div>;
            })}
            activeTab={activeTab}
            onTabChange={(index) => dispatch(setActiveTab({ index }))}
            isDeletable={tabs.map((_, index) => index >= defaultTabsCount)}
            onRemoveTab={handleRemoveTab}
          />
        )}
      </div>
    </div>
  );
};

export default ManageTeamDetails;